import React, { useState, useEffect } from "react";
import logo from "../../assests/images/candidate-headshot-new.png";
import { Box, Typography, Button, Container, Stack } from "@mui/material";
import { getPetitionCount } from "../../apis/user-apis/petitionSectionApis";
const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
};

const CampaignHero = () => {
  const [petitionCount, setPetitionCount] = useState(0);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await getPetitionCount();
        setPetitionCount(data.count || 0);
      } catch (err) {
        console.error("Failed to fetch petition count:", err);
      }
    };
    fetchCount();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#0A192F",
        backgroundImage:
          "radial-gradient(circle at 20% 50%, #112240 0%, #0A192F 100%)",
        color: "#FFF",
        pt: { xs: "100px", md: "140px" }, // ← accounts for fixed navbar + email bar on mobile
        pb: 8,
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth="lg">
        {/* ── Main row: text left, photo right ─────────────────── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 4, md: 6 },
            alignItems: { xs: "center", md: "center" },
          }}
        >
          {/* ── Left Content ───────────────────────────────────── */}
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: "#C59324",
                fontWeight: 700,
                letterSpacing: "0.2em",
                display: "block",
                mb: 2,
                fontSize: "0.85rem",
              }}
            >
              GARY TAYLOR · DELAWARE HOUSE DISTRICT 8 · MIDDLETOWN
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontWeight: 700,
                fontSize: { xs: "2.2rem", sm: "3rem", md: "5rem" },
                lineHeight: 1.1,
              }}
            >
              MAKE IT
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                color: "#C59324",
                fontSize: { xs: "2.2rem", sm: "3rem", md: "5rem" },
                mt: -1,
              }}
            >
              Make Sense
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mt: 4,
                opacity: 0.9,
                fontWeight: 400,
                maxWidth: "500px",
                fontSize: { xs: "1rem", md: "1.1rem" },
                mx: { xs: "auto", md: 0 },
              }}
            >
              Common-sense leadership for Middletown. <br />
              Your neighbor. Not a politician.
            </Typography>

            {/* ── CTA Buttons ──────────────────────────────────── */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                mt: 5,
                alignItems: { xs: "stretch", sm: "center" },
                flexWrap: "wrap",
                rowGap: 1.5,
              }}
            >
              <Button
                variant="contained"
                onClick={() => scrollTo("#donate")}
                sx={{
                  bgcolor: "#C59324",
                  color: "#000",
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  minWidth: "140px",
                  "&:hover": { bgcolor: "#A67C1E" },
                }}
              >
                Donate
              </Button>

              <Button
                variant="outlined"
                onClick={() => scrollTo("#volunteer")}
                sx={{
                  color: "#FFF",
                  borderColor: "#FFF",
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  minWidth: "140px",
                  "&:hover": { borderColor: "#C59324", color: "#C59324" },
                }}
              >
                Volunteer
              </Button>

              <Button
                variant="outlined"
                onClick={() => scrollTo("#petition")}
                sx={{
                  color: "#FF4D4D",
                  borderColor: "#4D1A1A",
                  bgcolor: "rgba(77, 26, 26, 0.3)",
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  textTransform: "none",
                }}
              >
                Sign the School Tax Petition
              </Button>
            </Stack>

            {/* ── Stats ────────────────────────────────────────── */}
            <Box
              sx={{
                mt: 8,
                pt: 4,
                borderTop: "1px solid rgba(255,255,255,0.1)",
                display: "flex",
                gap: { xs: 2, sm: 3, md: 5 },
                flexWrap: "wrap",
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: "center",
              }}
            >
              {[
                { val: "57", label: "Years in Delaware" },
                { val: "29", label: "Years in Middletown" },
                { val: "30+", label: "Years Law Enforcement" },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{ textAlign: { xs: "center", md: "left" } }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#C59324",
                      fontWeight: "bold",
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    {stat.val}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Right Image Card ──────────────────────────────── */}
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "340px", md: "420px" },
              backgroundColor: "#112240",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.08)",
              overflow: "hidden",
              boxShadow: "0 25px 60px rgba(0,0,0,0.4)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Photo */}
            <Box
              sx={{
                width: "100%",
                flex: 1,
                aspectRatio: "16/10",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Gary Taylor"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "top center",
                  display: "block",
                }}
              />
            </Box>

            {/* Card footer */}
            <Box
              sx={{
                backgroundColor: "#162848",
                padding: "14px 18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "14px",
                  letterSpacing: "1.5px",
                }}
              >
                GARY TAYLOR
              </Typography>
              <Box
                sx={{
                  backgroundColor: "rgba(201,150,42,0.15)",
                  border: "1px solid rgba(201,150,42,0.3)",
                  color: "#C59324",
                  padding: "4px 10px",
                  borderRadius: "2px",
                  fontSize: "11px",
                  fontFamily: "'DM Mono', 'Courier New', monospace",
                  letterSpacing: "1px",
                }}
              >
                FOR MIDDLETOWN
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── Petition Banner with LIVE counter ─────────────────── */}
        <Box
          sx={{
            mt: 8,
            bgcolor: "rgba(77, 26, 26, 0.4)",
            border: "1px solid #4D1A1A",
            borderRadius: "4px",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            🔥{" "}
            <span style={{ color: "#FF4D4D", fontWeight: "bold" }}>
              {petitionCount} District 8 residents
            </span>{" "}
            have signed: Stop the No-Vote Appoquinimink School Tax Increase
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => scrollTo("#petition")}
            sx={{
              bgcolor: "#E33E3E",
              textTransform: "none",
              fontWeight: "bold",
            }}
          >
            Add Your Name →
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default CampaignHero;
